'use client'

import Link from 'next/link'
import Image from 'next/image'
import { motion } from 'framer-motion'
import { BlogPost } from '@/lib/types'
import { FiCalendar, FiClock, FiArrowRight, FiBookOpen } from 'react-icons/fi'
import { formatDate } from '@/lib/utils'

interface BlogCardProps {
  post: BlogPost
}

export function BlogCard({ post }: BlogCardProps) {
  return (
    <motion.article
      whileHover={{ y: -8 }}
      className="bg-white rounded-2xl overflow-hidden shadow-lg shadow-slate-200/50 hover:shadow-xl hover:shadow-slate-200/80 transition-all duration-300 h-full flex flex-col group"
    >
      {/* Featured Image */}
      <Link 
        href={`/blog/${post.slug}`}
        className="relative h-52 bg-gradient-to-br from-teal-100 to-teal-200 overflow-hidden"
      >
        {post.featuredImage ? (
          <Image
            src={post.featuredImage}
            alt={post.title}
            fill
            className="object-cover group-hover:scale-110 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FiBookOpen className="w-16 h-16 text-teal-300" />
          </div>
        )}
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm text-teal-700 text-xs font-semibold">
            {post.category}
          </span>
        </div>
      </Link>

      {/* Content */}
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
          <span className="flex items-center gap-1">
            <FiCalendar className="w-4 h-4" />
            {formatDate(post.publishedAt)}
          </span>
          <span className="flex items-center gap-1">
            <FiClock className="w-4 h-4" />
            {post.readTime} min read
          </span>
        </div>

        <Link href={`/blog/${post.slug}`} className="block mb-3">
          <h3 className="text-xl font-bold text-slate-900 group-hover:text-teal-600 transition-colors line-clamp-2">
            {post.title}
          </h3>
        </Link>

        <p className="text-slate-600 mb-6 line-clamp-3 flex-1">
          {post.excerpt || post.content?.slice(0, 150)}
        </p>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {post.tags.slice(0, 3).map((tag, index) => (
              <span 
                key={index} 
                className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-md"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Read More */}
        <Link
          href={`/blog/${post.slug}`}
          className="inline-flex items-center gap-2 text-teal-600 font-semibold group-hover:gap-3 transition-all"
        >
          Read More
          <FiArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </motion.article>
  )
}
